import { makeStyles, Container } from '@material-ui/core'
import { useTranslation } from 'next-i18next'
import T, { specularRing, specularHost, specularRingReduced } from '../../home-new/tokens'
import Reveal from '../../home-new/Reveal'

// Hero for /tracking?new (design mockup: new-design-other/tracking/single.png).
//
// hero-boxes.png/.webp IS the full hero banner (2241×786): it already carries
// the soft light-blue office background + the white SHIPPOP boxes cluster on
// the RIGHT — the LEFT ~60% is empty light-blue space, deliberately, so the
// heading/copy can overlay it. So it's shown FULL-WIDTH edge-to-edge (same
// treatment as components/pages-new/service-new/ServiceHero.js's hero.png)
// rather than a separately-scaled/positioned element sitting over its own
// gradient — that earlier approach made the banner's own light-blue left
// edge collide with the page's `T.gradient.hero` background, producing a
// visible seam. Showing the whole banner as one continuous image (its own
// background IS the hero background) removes the seam entirely, matching the
// mockup. A floating white "folder tab" search card (domestic/inter tabs +
// full-width tracking-number input + pill submit) overlaps the banner's
// bottom edge — visual language reused 1:1 from
// components/home-new/CheckpriceFormNew.js's tab/card treatment.
//
// NOTE on breakpoints: ServiceHero.js pairs `up('md')`/`down('md')` for its
// desktop/mobile split. MUI v4's `breakpoints.down(key)` actually resolves to
// the *next* breakpoint's value minus an epsilon (`down('md')` → <1280px),
// not <960px — so pairing it with `up('md')` (>=960px) leaves BOTH rule sets
// active for the 960–1279px band (e.g. the 1024px checkpoint), which is why
// this file uses `down('sm')` (the true <960px complement of `up('md')`)
// instead, as fixed here previously. Structure below otherwise mirrors
// ServiceHero exactly.
//
// Purely presentational/controlled — all state (trackingMode, trackingNumber),
// the Axios search dispatch and the search-limit Popover anchor live in the
// parent (TrackingNew.js) and are unchanged; this component only renders UI
// and calls back via props.
const TABS = [
  {
    key: 'domestic',
    icon: '/static/img/home/checkprice-icon-truck.svg',
  },
  {
    key: 'inter',
    icon: '/static/img/home/checkprice-icon-plane.svg',
  },
]

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    background: T.gradient.hero,
    // Bleed the light-blue background AND the banner scene itself up behind
    // the fixed HeaderNew (cancel NewShell's main paddingTop:92 via a matching
    // negative margin) — unlike ServiceHero/HeroNew, the tracking mockup
    // wants the banner's blurred-office top showing straight through behind
    // the header, with no plain gradient strip above it. No paddingTop here:
    // `bannerImg` stays in normal flow (see below) so it starts flush at
    // root's own top edge, i.e. right where the header sits (z-index 1100
    // draws the header over it). The `overlay` text layer is positioned
    // independently via its own `top` below — being `position:absolute`,
    // its containing block is root's padding box, so it clears the header
    // regardless of root having no padding of its own.
    [theme.breakpoints.up('md')]: {
      marginTop: -92,
    },
    // Mobile: banner as a full cover background with the copy on a frosted
    // panel over it (ServiceHero's mobile treatment).
    [theme.breakpoints.down('sm')]: {
      minHeight: 'clamp(480px, 118vw, 620px)',
    },
  },
  // Full-width banner. Its natural aspect (~2.85:1) drives the hero height on
  // desktop so the whole scene (empty light-blue left + boxes on the right)
  // shows uncropped, edge-to-edge — no separate background layer behind it.
  bannerImg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      // Boxes cluster sits in the right ~40% of the source photo — bias the
      // mobile cover-crop toward it instead of the empty left area.
      objectPosition: '68% center',
      marginTop: 0,
      zIndex: 0,
    },
  },
  // Desktop: absolute layer over the banner's empty left area, copy anchored
  // to the upper third (not vertically centered — see below).
  // Mobile: frosted panel anchored near the bottom of the cover-cropped banner.
  overlay: {
    // `flex-start` (not `center`) + the extra paddingTop below puts the copy
    // in the banner's UPPER third, like the mockup — vertical-centering made
    // the (multi-line) description sit low enough that the search card's
    // negative-margin overlap collided with its last line instead of just
    // the banner underneath it.
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      top: 120,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'flex-start',
      paddingTop: 24,
      zIndex: 2,
      pointerEvents: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'flex-end',
      zIndex: 2,
      paddingTop: 90,
      paddingBottom: 22,
      pointerEvents: 'none',
    },
  },
  overlayInner: { width: '100%' },
  copy: {
    // 760 so the subheading fits on ONE line like the mockup at real desktop
    // widths instead of wrapping to two.
    maxWidth: 760,
    pointerEvents: 'auto',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      textAlign: 'center',
      background: 'rgba(255,255,255,0.80)',
      WebkitBackdropFilter: 'blur(10px)',
      backdropFilter: 'blur(10px)',
      borderRadius: 24,
      padding: '22px 20px 24px',
      boxShadow: '0 16px 44px rgba(0,102,204,0.16)',
      border: '1px solid rgba(255,255,255,0.6)',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 56,
    fontWeight: 700,
    lineHeight: 1.15,
    color: T.color.ink,
    margin: '0 0 18px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 30,
      margin: '0 0 12px',
    },
  },
  // "เช็กสถานะ" only — reactbits "Gradient Text" treatment copied verbatim
  // from components/home-business/HeroBiz.js's heading `& span` (same blue
  // gradient family, same 300%-wide background + businessFlow-style
  // ping-pong keyframe drift, same progressive-enhancement fallback color
  // before the background-clip:text reveal). "พัสดุ" stays outside this
  // span so it inherits `heading`'s plain ink color.
  gradWord: {
    color: T.color.brand,
    backgroundImage:
      'linear-gradient(to right, #0066CC, #009EFF, #33C9FF, #66D9FF, #33C9FF, #009EFF, #0066CC)',
    backgroundSize: '300% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: '$trackFlow 8s linear infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes trackFlow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  sub: {
    fontFamily: T.font.heading,
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.5,
    color: T.color.ink,
    margin: '0 0 14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      margin: '0 0 10px',
    },
  },
  desc: {
    fontFamily: T.font.body,
    fontSize: 16,
    lineHeight: 1.8,
    color: T.color.muted,
    margin: 0,
    maxWidth: 640,
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      maxWidth: '100%',
    },
  },
  // Card wrapper sits BELOW the hero (own stacking context) so it can overlap
  // the hero's bottom edge with a negative margin — same "floating over the
  // fold" feel as the design mockup, without needing to know the hero's exact
  // rendered height. -130 pulled the card up far enough to collide with the
  // (now upper-third-positioned, but still multi-line) description's last
  // line; -80 still overlaps the banner's lower portion like the mockup
  // while clearing the copy with comfortable room. marginBottom sets the
  // rhythm before the TQM promo banner (TrackingPromoNew), which also has
  // its own top padding.
  cardOuter: {
    position: 'relative',
    zIndex: 3,
    [theme.breakpoints.up('md')]: {
      // Pull the card UP so it tucks tightly under the description and overlaps
      // more of the banner (user preference — tighter than the literal mockup
      // ~45px). Target rendered desc-bottom→card-top gap ≈ 16px, HARD FLOOR
      // 14px so the tabs never touch "...ผ่านระบบ SHIPPOP".
      //
      // WHY calc(vw), not a fixed px: the card sits in normal flow after the
      // hero, so its top = bannerBottom + marginTop, and the full-bleed banner's
      // height scales with viewport width (aspect 2241×786 → height ≈ 0.3507 ×
      // width ≈ 35.07vw). The description, however, is in the absolute overlay
      // at a FIXED top, so its bottom is width-stable. A FIXED negative
      // marginTop therefore gives a wildly different gap per width (measured at
      // -124: gap −40px @1280 = OVERLAP, 16px @1440, 100px @1680). Expressing the
      // pull-up as `381px − 35.07vw` cancels the banner's width-scaling so the
      // gap stays ~16px at every width (verified @1280/1440/1680; floor held).
      marginTop: 'calc(381px - 35.07vw)',
      marginBottom: 56,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
      marginBottom: 28,
    },
  },
  tabRow: {
    display: 'flex',
    gap: '6px',
    marginBottom: -2,
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('xs')]: {
      gap: '3px',
    },
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 30px',
    border: 'none',
    borderRadius: '16px 16px 0 0',
    cursor: 'pointer',
    fontFamily: T.font.heading,
    fontSize: 17,
    fontWeight: 500,
    lineHeight: 1,
    background: 'rgba(233, 243, 255, 0.75)',
    color: T.color.muted,
    transition: 'background 0.25s ease, color 0.25s ease',
    touchAction: 'manipulation',
    '& img': {
      width: 26,
      height: 26,
      objectFit: 'contain',
      flexShrink: 0,
    },
    '&:hover': {
      background: 'rgba(233, 243, 255, 1)',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '10px 16px',
      fontSize: 14,
      flex: 1,
      justifyContent: 'center',
      '& img': { width: 20, height: 20 },
    },
  },
  // Glass fill (same as `card` below, and the same treatment the /new
  // check-price hero card uses) instead of solid white — the active tab now
  // sits over the banner photo (card overlaps it), so it needs to read as
  // translucent too rather than an opaque patch breaking the glass illusion.
  tabActive: {
    background: T.glass.panel,
    backdropFilter: T.glass.blur,
    WebkitBackdropFilter: T.glass.blur,
    color: T.color.ink,
    boxShadow: 'none',
    '&:hover': {
      background: T.glass.panel,
    },
  },
  // Frosted-glass panel (design mockup: the card overlaps the banner photo,
  // which shows faintly through it) instead of solid white — same
  // `T.glass.panel`/`T.glass.blur` treatment as CheckpriceFormNew's
  // check-price hero card.
  card: {
    background: T.glass.panel,
    backdropFilter: T.glass.blur,
    WebkitBackdropFilter: T.glass.blur,
    borderRadius: '0 24px 24px 24px',
    boxShadow: T.shadow.lg,
    padding: '26px 32px 28px',
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '18px',
    [theme.breakpoints.down('xs')]: {
      padding: '20px 18px 24px',
      borderRadius: '0 0 18px 18px',
    },
  },
  trackInput: {
    // Full card width (no maxWidth cap) — the mockup's input spans
    // essentially the whole card, not a narrow centered box. The submit
    // button below stays visually centered via minWidth + the card's
    // `alignItems: center`.
    width: '100%',
    height: 56,
    padding: '0 20px',
    fontSize: 16,
    fontFamily: T.font.body,
    color: T.color.ink,
    background: '#fff',
    border: `1.5px solid ${T.color.line}`,
    borderRadius: 12,
    outline: 'none',
    touchAction: 'manipulation',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      borderColor: T.color.brand,
    },
    '&::placeholder': {
      color: T.color.faint,
    },
  },
  trackBtn: {
    ...specularHost,
    minWidth: 200,
    height: 52,
    border: 'none',
    borderRadius: 999,
    background: T.gradient.blue,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 17,
    fontWeight: 600,
    cursor: 'pointer',
    touchAction: 'manipulation',
    transition: 'filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
    '&::before': specularRing(T.ring.blue),
    '&:hover': {
      filter: 'brightness(1.05)',
      transform: 'translateY(-2px)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      ...specularRingReduced,
    },
  },
}))

const TrackingHeroNew = ({
  trackingMode,
  onModeChange,
  trackingNumber,
  onNumberChange,
  onSubmit,
  searchFieldRef,
  domesticLabel,
  interLabel,
  placeholder,
  submitLabel,
}) => {
  const classes = useStyles()
  const { t } = useTranslation('tracking')

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onSubmit()
    }
  }

  return (
    <>
      <section className={classes.root}>
        <div className={classes.overlay}>
          <Container
            maxWidth={false}
            style={{ maxWidth: T.container }}
            className={classes.overlayInner}
          >
            <div className={classes.copy}>
              <Reveal direction="left">
                <h1 className={classes.heading}>
                  <span className={classes.gradWord}>{t('new.hero.heading-grad')}</span>
                  {t('new.hero.heading-rest')}
                </h1>
              </Reveal>
              <Reveal direction="left" delay={0.08}>
                <p className={classes.sub}>{t('new.hero.sub')}</p>
              </Reveal>
              <Reveal direction="left" delay={0.14}>
                <p className={classes.desc}>{t('new.hero.desc')}</p>
              </Reveal>
            </div>
          </Container>
        </div>
        <picture>
          <source
            srcSet="/static/img/tracking-new/hero-boxes.webp"
            type="image/webp"
          />
          <img
            className={classes.bannerImg}
            src="/static/img/tracking-new/hero-boxes.png"
            alt={t('new.hero.img-alt')}
            width={2241}
            height={786}
            fetchpriority="high"
            decoding="async"
          />
        </picture>
      </section>

      <Container
        maxWidth={false}
        style={{ maxWidth: T.container }}
        className={classes.cardOuter}
      >
        <Reveal direction="up" delay={0.2}>
          <div className={classes.tabRow} role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                type="button"
                aria-selected={trackingMode === tab.key}
                className={`${classes.tab} ${
                  trackingMode === tab.key ? classes.tabActive : ''
                }`}
                onClick={() => onModeChange(tab.key)}
              >
                <img src={tab.icon} alt="" aria-hidden="true" decoding="async" />
                {tab.key === 'domestic' ? domesticLabel : interLabel}
              </button>
            ))}
          </div>
          <div className={classes.card}>
            <input
              ref={searchFieldRef}
              className={classes.trackInput}
              type="text"
              inputMode="text"
              enterkeyhint="search"
              placeholder={placeholder}
              aria-label={placeholder}
              value={trackingNumber || ''}
              onChange={(e) => onNumberChange(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button type="button" className={classes.trackBtn} onClick={onSubmit}>
              {submitLabel}
            </button>
          </div>
        </Reveal>
      </Container>
    </>
  )
}

export default TrackingHeroNew
